import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import type { FeatureCollection, Point } from "geojson";

import coords from "./coords.json";

mapboxgl.accessToken = "pk.eyJ1IjoieGR1cmFuZCIsImEiOiJjbWN5azNxZG8wbm53MmtzM2cxeTM0MTQyIn0.BTwzrXnWc21Gs6ASPWVMIA";

const App: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (mapContainer.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [-100, 20],
        zoom: 1.5,
        maxZoom: 7,
      });

      // Load coordinates and add heatmap layer
      const features = coords.map((c: { lng: number; lat: number }) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [c.lng, c.lat],
        },
        properties: {},
      }));
      const geojson: FeatureCollection<Point> = {
        type: "FeatureCollection",
        features,
      };
      if (!mapRef.current) {
        return;
      }
      mapRef.current.on("load", () => {
        if (!mapRef.current) return;
        if (!mapRef.current.getSource("users")) {
          mapRef.current.addSource("users", {
            type: "geojson",
            data: geojson,
          });
          mapRef.current.addLayer({
            id: "users-heatmap",
            type: "heatmap",
            source: "users",
            paint: {
              "heatmap-weight": 0.75,
              "heatmap-intensity": 1,
              "heatmap-radius": 20,
              "heatmap-opacity": 0.8,
              "heatmap-color": [
                "interpolate",
                ["linear"],
                ["heatmap-density"],
                0,
                "rgba(0,0,0,0)",
                0.2,
                "#ffedfd",
                0.33,
                "#fdb022",
                0.66,
                "#f45555",
                1,
                "#ff4beb",
              ],
            },
          });
        }
      });
      // '50': '#fff6fe',
      // '100': '#ffedfd',
      // '200': '#ffe3fc',
      // '300': '#ffc9f9',
      // '400': '#ffb0f6',
      // '500': '#ff96f4',
      // '600': '#ff92f4',
      // '700': '#ff6ef1',
      // '800': '#ff4beb',
      // '900': '#e544d6',
      // '1000': '#cc3dbe',
      // '1100': '#b235a6',
      // '1200': '#992d8e',
      // '1300': '#802577',
      // '1400': '#661e5f',

      // At low zooms, complete a revolution every two minutes.
      const secondsPerRevolution = 120;
      // Above zoom level 5, do not rotate.
      const maxSpinZoom = 5;
      // Rotate at intermediate speeds between zoom levels 3 and 5.
      const slowSpinZoom = 3;

      let userInteracting = false;
      let spinEnabled = true;

      function spinGlobe() {
        const zoom = mapRef.current?.getZoom();
        if (spinEnabled && !userInteracting && zoom && zoom < maxSpinZoom) {
          let distancePerSecond = 360 / secondsPerRevolution;
          if (zoom > slowSpinZoom) {
            // Slow spinning at higher zooms
            const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
            distancePerSecond *= zoomDif;
          }
          const center = mapRef.current?.getCenter();
          if (center) {
            center.lng += distancePerSecond;
            // Smoothly animate the map over one second.
            // When this animation is complete, it calls a 'moveend' event.
            mapRef.current?.easeTo({ center, duration: 1000, easing: (n) => n });
          }
        }
      }

      // Pause spinning on interaction
      mapRef.current?.on("mousedown", () => (userInteracting = true));
      mapRef.current?.on("touchstart", () => (userInteracting = true));
      mapRef.current?.on("touchmove", () => (userInteracting = true));

      // Restart spinning the globe when interaction is complete
      mapRef.current?.on("mouseup", () => {
        userInteracting = false;
        spinGlobe();
      });

      // These events account for cases where the mouse has moved
      // off the map, so 'mouseup' will not be fired.
      mapRef.current?.on("dragend", () => {
        userInteracting = false;
        spinGlobe();
      });
      mapRef.current?.on("touchend", () => {
        userInteracting = false;
        spinGlobe();
      });
      mapRef.current?.on("pitchend", () => {
        userInteracting = false;
        spinGlobe();
      });
      mapRef.current?.on("rotateend", () => {
        userInteracting = false;
        spinGlobe();
      });

      // When animation is complete, start spinning if there is no ongoing interaction
      mapRef.current?.on("moveend", () => {
        spinGlobe();
      });

      spinGlobe();
    }
    return () => {
      mapRef.current?.remove();
    };
  }, []);

  return (
    <div ref={mapContainer} style={{ width: "100vw", height: "100vh" }} />
  );
};

export default App;
